import {twMerge} from '@sdox/utils'

import {ResponsiveMedia} from '../ResponsiveMedia'
import {RichText} from '../RichText'

import type {ClassNameValue} from 'tailwind-merge'
import type {ReactNode} from 'react'
import type {ResponsiveMediaType} from '../ResponsiveMedia'

export interface TextImageProps {
  aspectRatio?: '1/1' | '2/3' | '3/2'
  className?: ClassNameValue
  mediaPosition?: 'leading' | 'trailing'
  headline?: string
  media: ResponsiveMediaType
  callout?: string
  cta?: ReactNode
  showBottomBorder?: boolean
}

export const TextImage = (props: TextImageProps) => {
  const {
    aspectRatio = '3/2',
    callout,
    className,
    cta,
    headline,
    media,
    mediaPosition,
    showBottomBorder = false,
  } = props

  const wrapperClassName = twMerge(
    'grid grid-cols-1 gap-spacing-24 overflow-hidden',
    '@[704px]:grid-cols-2 @[704px]:gap-block-small',
    showBottomBorder && 'border-b-1 border-outline-secondary pb-block-medium @[704px]:border-b-0 @[704px]:pb-0',
    className,
  )

  const mediaWrapperClassName = twMerge(
    'h-fit w-full',
    aspectRatio === '1/1' && 'aspect-[1/1]',
    aspectRatio === '2/3' && 'aspect-[2/3]',
    aspectRatio === '3/2' && 'aspect-[3/2]',
    mediaPosition === 'leading' && 'order-first',
    mediaPosition === 'trailing' && 'order-first @[704px]:order-last',
  )

  return (
    <div className={'@container/block'}>
      <div className={wrapperClassName}>
        <div className={mediaWrapperClassName}>
          <ResponsiveMedia
            media={media}
            // Set screen size to desktop because TextImage does not support mobile media yet.
            currentScreenSize={'desktop'}
            classNames={{
              image: 'rounded-radius-md size-full object-cover object-center',
              video: 'rounded-radius-md',
            }}
          />
        </div>
        <div
          className={'flex flex-col justify-center gap-spacing-12 @[704px]:px-spacing-40 @[704px]:py-spacing-16'}
        >
          {headline ? (
            <RichText
              as={'h4'}
              className={'text-h4'}
              content={headline}
            />
          ) : null}
          {callout && (
            <RichText
              as={'div'}
              className={'text-p1'}
              content={callout}
            />
          )}
          {cta && (
            <div className={'self-start pt-spacing-16'}>
              {cta}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
